import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AccountSettingsService, IAccountSettingsContent } from "@app/services/account-settings.service";
import { DisplayService, IDialogOptionsJson } from "@app/services/display.service";
import { TranslationService } from "@app/services/translation.service";
import { copy } from "angular";
import './account-settings-manager.component.scss';

enum AccountSettingsFormState {
    Loading,
    Loaded,
    Saving,
    Saved
}

@Component({
    selector: 'account-settings-manager',
    template: require('./account-settings-manager.component.html')
})
export class AccountSettingsManagerComponent implements OnInit {

    defaultAccountSettings: IAccountSettingsContent;
    accountSettings: IAccountSettingsContent;

    accountSettingsForm: FormGroup;

    formChanged: boolean;
    formState: AccountSettingsFormState = AccountSettingsFormState.Loading;

    AccountSettingsFormState = AccountSettingsFormState;

    enableLiveView: boolean;
    enableNonEventDownloads: boolean;
    enableVideoRedaction: boolean;
    enableVideoMute: boolean;

    constructor(
        private readonly accountSettingsService: AccountSettingsService,
        private readonly formBuilder: FormBuilder,
        private readonly displayService: DisplayService,
        private readonly translationService: TranslationService
    ) {
    }

    ngOnInit() {
        this.getData();
    }

    onFormChanged(): void {
        this.formState = AccountSettingsFormState.Loaded;
        setTimeout(() => {
            this.formChanged = this.checkIfFormValuesChanged();
        });
    }

    discardChanges(): void {
        this.reset();
    }

    onSaveButtonClicked(event: MouseEvent): void {
        this.showDeleteDialog(event);
    }

    onDisableLiveViewChanged(value: boolean): void {
        this.enableLiveView = value;
        this.onFormChanged();
    }

    onDisableNonEventChanged(value: boolean): void {
        this.enableNonEventDownloads = value;
        this.onFormChanged();
    }

    onEnableVideoRedactionChanged(value: boolean): void {
        this.enableVideoRedaction = value;
        this.onFormChanged();
    }

    onEnableVideoMuteChanged(value: boolean): void {
        this.enableVideoMute = value;
        this.onFormChanged();
    }

    //--

    private save(): void {

        this.formState = AccountSettingsFormState.Saving;

        const dirtyAccountSettings = this.createAccountSettingsContentFromForm();

        this.accountSettingsService.addOrUpdateAccountSettings(dirtyAccountSettings).finally(() => {

            this.accountSettings = copy(dirtyAccountSettings);

            setTimeout(() => {
                this.clearFormChanged();
                this.formState = AccountSettingsFormState.Saved;
            }, 500);

        });
    }

    private reset(): void {
        this.formState = AccountSettingsFormState.Loaded;

        this.updateLocalValuesForCameraSettings();
        this.clearFormChanged();
        this.buildForm();
    }

    private clearFormChanged(): void {
        this.formChanged = false;
    }

    private async getData(): Promise<void> {

        this.formState = AccountSettingsFormState.Loading;

        const promises: Promise<any>[] = [
            this.accountSettingsService.getDefaultAccountSettings(),
            this.accountSettingsService.getAccountSettings()
        ];

        await Promise.all(promises).then(([defaultAccountSettings, accountSettings]) => {
            this.defaultAccountSettings = defaultAccountSettings;
            this.accountSettings = accountSettings;

            this.updateLocalValuesForCameraSettings();
        });

        this.buildForm();

        this.formState = AccountSettingsFormState.Loaded;
    }

    private updateLocalValuesForCameraSettings(): void {
        this.enableLiveView = !this.getInitialFormValue("DisableLiveView") as boolean;
        this.enableNonEventDownloads = !this.getInitialFormValue("DisableNonEventDownloads") as boolean;
        this.enableVideoRedaction = this.getInitialFormValue("EnableVideoRedaction") as boolean;
        this.enableVideoMute = this.getInitialFormValue("EnableVideoMute") as boolean;
    }

    private buildForm(): void {
        this.accountSettingsForm = this.formBuilder.group({
            videoRetentionDays: [this.getInitialFormValue("VideoRetentionDays"), [Validators.required, Validators.min(1), Validators.max(365)]],   //video retention
            enableLiveView: [this.enableLiveView],
            enableNonEventDownloads: [this.enableNonEventDownloads],
            enableVideoRedaction: [this.enableVideoRedaction],
            enableVideoMute: [this.enableVideoMute]
        });
    }

    private getInitialFormValue(key: string): any {

        if (this.accountSettings?.[key]) {
            return this.accountSettings[key].Value;
        }

        if (this.defaultAccountSettings?.[key]) {
            return this.defaultAccountSettings[key].Value;
        }

        return 0;
    }

    private checkIfFormValuesChanged(): boolean {

        const formValueString = JSON.stringify(this.createAccountSettingsContentFromForm());
        const originalAccountSettingsString = JSON.stringify(this.accountSettings);

        return formValueString !== originalAccountSettingsString;
    }

    private createAccountSettingsContentFromForm(): IAccountSettingsContent {
        const accountSettingsContent: IAccountSettingsContent = {
            VideoRetentionDays: {
                Value: this.accountSettingsForm.get("videoRetentionDays").value 
            },
            DisableLiveView: {
                Value: !this.enableLiveView  
            },
            DisableNonEventDownloads: {
                Value: !this.enableNonEventDownloads
            },
            EnableVideoRedaction: {
                Value: this.enableVideoRedaction
            },
            EnableVideoMute: {
                Value: this.enableVideoMute
            }
        };

        return accountSettingsContent;
    }

    private showDeleteDialog($event): void {
        const title = this.translationService.t("AccountSettingsManager.SaveWarningDialogTitle");
        const dialogHTML = `<account-settings-save-warning (response)="$ctrl.onResponse($event); $ctrl.hide();"></account-settings-save-warning>`

        const dialogOptions: IDialogOptionsJson = {
            title: title,
            dialogHTML: dialogHTML,
            dialogId: `accountSettingsSaveWarningDialog`,
            bindings: {
                onResponse: (confirmed: boolean) => { this.onSaveDialogResponse(confirmed); }
            },
            event: $event,
            multiple: true,
            clickOutsideToClose: true,
            escapeToClose: true
        };

        this.displayService.showDialog(dialogOptions);

    }

    onSaveDialogResponse(confirmed: boolean) {
        if (!confirmed) {
            return;
        }

        this.save();
    }

}
